import { Box } from '@mui/material';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import type { CohortChartConfig, DataPoint } from '@/features/chartsView';
import {
  LineChartWrapper,
  TABLEAU_20_COLORS,
  useHoveredSplitOption,
  resolveTooltipMetrics,
  useLineChartParams,
} from '@/features/chartsView';
import { useDataFetcher } from '@/shared/hooks';
import type { DataStatus } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import { isDateDimension, isMediaDimension } from '@/shared/types';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import { selectMeta } from '../../model/slice';
import type { CohortDataResponse } from '../../model/types';

const SeriesChart = lazy(() =>
  import('@/features/chartsView').then(({ SeriesChart }) => {
    return {
      default: SeriesChart<typeof CHART_BASE_DIMENSION>,
    };
  }),
);

interface Props {
  config: CohortChartConfig;
  optionColors?: ReadonlyMap<string, string>;
  onGetData: () => Promise<CohortDataResponse>;
  onDataChange?: (data?: CohortDataResponse) => void;
  onDataStatusChange?: (dataStatus: DataStatus) => void;
}

export function Chart({
  config,
  optionColors,
  onGetData,
  onDataChange,
  onDataStatusChange,
}: Props) {
  const cohortMeta = useAppSelector(selectMeta);
  const isMediaContent = cohortMeta
    ? isMediaDimension(cohortMeta.config, config.splitDimension)
    : false;
  const isDateContent = cohortMeta
    ? isDateDimension(cohortMeta.config, config.splitDimension)
    : false;
  const hoveredOption = useHoveredSplitOption();
  const { data, dataStatus } = useDataFetcher(onGetData, {
    keepPreviousData: true,
  });
  const splitOptions = useMemo(
    () => data?.splitOptions?.[config.splitDimension] ?? [],
    [data, config.splitDimension],
  );
  const {
    noData,
    lines,
    leftY,
    rightY,
    tooltipYs,
    data: chartData,
  } = useLineChartParams({
    meta: cohortMeta,
    chart: config,
    data: (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
    splitOptions,
    defaultSplitOption: CHART_BASE_DIMENSION,
    excludedSplitOptions: config.excludedSplitOptions,
    tooltipMetrics: resolveTooltipMetrics(config),
  });

  useEffect(() => {
    onDataChange?.(data);
  }, [data, onDataChange]);

  useEffect(() => {
    onDataStatusChange?.(dataStatus);
  }, [dataStatus, onDataStatusChange]);

  const noSelection = lines.length === config.excludedSplitOptions.length;
  const hasCompleteChart =
    !!data && !noData && !leftY?.noData && !rightY?.noData && !noSelection;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LineChartWrapper
        dataStatus={dataStatus}
        keepContentWhileLoading={hasCompleteChart}
        noData={noData}
        noSelection={noSelection}
      >
        <Suspense fallback={<Loader active />}>
          <SeriesChart
            sx={{ border: 'none' }}
            syncYAxesDomains={config.syncedYAxes}
            excludedLines={config.excludedSplitOptions}
            palette={TABLEAU_20_COLORS}
            optionColors={optionColors}
            highlightedLine={config.highlightLineOnHover ? hoveredOption : null}
            isMediaContent={isMediaContent}
            isDateContent={isDateContent}
            xAxisDataKey={CHART_BASE_DIMENSION}
            leftY={leftY}
            rightY={rightY}
            tooltipYs={tooltipYs}
            data={chartData}
            lines={lines}
            leftSeries={config.leftAxisSeries}
            rightSeries={config.rightAxisSeries}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
